"use client";

// Bundle of non-critical, late-mount client components for the root layout.
//
// Why this exists:
// - The root layout (src/app/layout.tsx) used to mount <Toaster />,
//   <INPMonitor />, <SpeedInsights />, and <Analytics /> directly, on every
//   route. Each one ships its own JS chunk and hydrates immediately, which
//   contributes to TBT (Total Blocking Time) -- the metric that was tanking
//   the /blog Lighthouse score.
// - None of these components are required for First Contentful Paint or LCP.
// - We can therefore wait until the browser is idle (or 2s elapses), then
//   load them lazily.
//
// Why this is safe:
// - sonner's `Toaster` queues any `toast()` calls made before it mounts and
//   renders them once it's ready, so deferring it never drops a toast.
// - Vercel `<SpeedInsights />` and `<Analytics />` both use web-vitals under
//   the hood, which subscribes to PerformanceObserver with `buffered: true`.
//   That means even when they mount ~2s late, they still capture the page's
//   LCP/CLS/FCP entries that already happened. No data loss.
// - INPMonitor only watches future interaction events, so a 2s delay only
//   misses the very first interaction (which is rare to happen pre-idle).

import dynamic from "next/dynamic";
import { IdleMount } from "@/components/ui/idle-mount";

// `ssr: false` ensures these never run during server render and don't
// contribute to the HTML payload. `loading: () => null` returns nothing while
// the chunk is being fetched (which only starts after IdleMount triggers).
const Toaster = dynamic(
  () => import("@/components/ui/sonner").then((m) => m.Toaster),
  { ssr: false, loading: () => null }
);

const INPMonitor = dynamic(
  () => import("@/components/ui/INPMonitor").then((m) => m.INPMonitor),
  { ssr: false, loading: () => null }
);

const SpeedInsights = dynamic(
  () => import("@vercel/speed-insights/next").then((m) => m.SpeedInsights),
  { ssr: false, loading: () => null }
);

const Analytics = dynamic(
  () => import("@vercel/analytics/next").then((m) => m.Analytics),
  { ssr: false, loading: () => null }
);

export function IdleClients() {
  return (
    <IdleMount>
      <Toaster />
      <INPMonitor />
      <SpeedInsights />
      <Analytics />
    </IdleMount>
  );
}
